import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Menu, X, User, LogOut, Home } from "lucide-react";
import { CountryToggle } from "../shared/CountryToggle";

const Header = () => {
  const { user, profile, signOut, isAuthenticated } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <header className="header">
      <div className="header-container">
        <Link to="/" className="logo" data-testid="logo">
          <div className="logo-icon">
            <Home size={20} />
          </div>
          <span className="logo-text"><span className="logo-handy">Handy</span><span className="logo-hub">Hub</span></span>
        </Link>

        <nav className="nav-desktop">
          <Link to="/categories" className="nav-link" data-testid="nav-categories">Categories</Link>
          <Link to="/services" className="nav-link" data-testid="nav-services">Services</Link>
          <Link to="/providers" className="nav-link" data-testid="nav-providers">Find Professionals</Link>
        </nav>

        <div className="header-actions">
          <CountryToggle />
          {isAuthenticated ? (
            <div className="user-menu">
              <button className="user-button" data-testid="user-menu-button">
                <User size={20} />
                <span>{profile?.full_name || 'Account'}</span>
              </button>
              <div className="user-dropdown">
                {profile?.role === 'customer' && (
                  <Link to="/my-quotes" className="dropdown-item" data-testid="my-quotes-link">My Quotes</Link>
                )}
                {profile?.role === 'provider' && (
                  <Link to="/provider/dashboard" className="dropdown-item" data-testid="provider-dashboard-link">Dashboard</Link>
                )}
                {profile?.role === 'admin' && (
                  <>
                    <Link to="/admin" className="dropdown-item" data-testid="admin-overview-link">Admin Dashboard</Link>
                    <Link to="/admin/providers" className="dropdown-item" data-testid="admin-link">Provider Management</Link>
                    <Link to="/admin/quotes" className="dropdown-item" data-testid="admin-quotes-link">Quote Monitoring</Link>
                    <Link to="/admin/subscriptions" className="dropdown-item" data-testid="admin-subscriptions-link">Subscriptions</Link>
                    <Link to="/admin/reviews" className="dropdown-item" data-testid="admin-reviews-link">Review Moderation</Link>
                    <Link to="/admin/categories" className="dropdown-item" data-testid="admin-categories-link">Category Management</Link>
                    <Link to="/admin/reset" className="dropdown-item" data-testid="admin-reset-link">Reset Test Data</Link>
                  </>
                )}
                <button onClick={handleSignOut} className="dropdown-item logout" data-testid="logout-button">
                  <LogOut size={16} />
                  Sign Out
                </button>
              </div>
            </div>
          ) : (
            <>
              <Link to="/login" className="btn-secondary" data-testid="login-button">Log In</Link>
              <Link to="/signup" className="btn-primary" data-testid="signup-button">Sign Up</Link>
            </>
          )}
        </div>

        <button 
          className="mobile-menu-btn" 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-button"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="mobile-menu">
          {isAuthenticated && (
            <div className="mobile-user-card" data-testid="mobile-user-card">
              <div className="mobile-user-avatar" aria-hidden="true">
                {profile?.avatar_url ? (
                  <img src={profile.avatar_url} alt="" />
                ) : (
                  <User size={18} />
                )}
              </div>
              <div className="mobile-user-info">
                <div className="mobile-user-name" data-testid="mobile-user-name">
                  {profile?.full_name || 'My account'}
                </div>
                <div className="mobile-user-meta">
                  <span className={`mobile-user-role mobile-user-role-${profile?.role || 'customer'}`} data-testid="mobile-user-role">
                    {profile?.role === 'admin' ? 'Admin'
                      : profile?.role === 'provider' ? 'Provider'
                      : 'Customer'}
                  </span>
                  {profile?.email && (
                    <span className="mobile-user-email">{profile.email}</span>
                  )}
                </div>
              </div>
            </div>
          )}
          <div className="mobile-country-toggle">
            <CountryToggle />
          </div>
          <Link to="/categories" className="mobile-link" onClick={() => setMobileMenuOpen(false)}>Categories</Link>
          <Link to="/services" className="mobile-link" onClick={() => setMobileMenuOpen(false)}>Services</Link>
          <Link to="/providers" className="mobile-link" onClick={() => setMobileMenuOpen(false)}>Find Professionals</Link>
          {isAuthenticated ? (
            <>
              {profile?.role === 'customer' && (
                <Link to="/my-quotes" className="mobile-link" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-my-quotes">My Quotes</Link>
              )}
              {profile?.role === 'provider' && (
                <Link to="/provider/dashboard" className="mobile-link" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-provider-dashboard">Dashboard</Link>
              )}
              {profile?.role === 'admin' && (
                <>
                  <Link to="/admin" className="mobile-link" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-admin-overview">Admin Dashboard</Link>
                  <Link to="/admin/providers" className="mobile-link" onClick={() => setMobileMenuOpen(false)}>Provider Management</Link>
                  <Link to="/admin/customers" className="mobile-link" onClick={() => setMobileMenuOpen(false)}>Customer Management</Link>
                  <Link to="/admin/quotes" className="mobile-link" onClick={() => setMobileMenuOpen(false)}>Quote Monitoring</Link>
                  <Link to="/admin/subscriptions" className="mobile-link" onClick={() => setMobileMenuOpen(false)}>Subscriptions</Link>
                  <Link to="/admin/reviews" className="mobile-link" onClick={() => setMobileMenuOpen(false)}>Review Moderation</Link>
                  <Link to="/admin/categories" className="mobile-link" onClick={() => setMobileMenuOpen(false)}>Category Management</Link>
                  <Link to="/admin/reset" className="mobile-link" onClick={() => setMobileMenuOpen(false)}>Reset Test Data</Link>
                </>
              )}
              <button onClick={() => { setMobileMenuOpen(false); handleSignOut(); }} className="mobile-link logout" data-testid="mobile-logout">
                <LogOut size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />
                Sign Out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="mobile-link" onClick={() => setMobileMenuOpen(false)}>Log In</Link>
              <Link to="/signup" className="mobile-link highlight" onClick={() => setMobileMenuOpen(false)}>Sign Up</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
};

export { Header };
